import { Boxes, CheckCircle2, Cpu, Server, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import type { CollectionResponse, DataRecord } from "../types";

interface Snapshot {
  models: number;
  tasks: number;
  activeTasks: number;
  servers: number;
  readyServers: number;
  failedOperations: number;
  recentOperations: DataRecord[];
}

export function OverviewPage() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [models, tasks, active, servers, ready, failed, operations] = await Promise.all([
        apiRequest<{ data: unknown[] }>("/api/ai-router/models"),
        apiRequest<CollectionResponse>("/api/ai-router/tasks?page=1&pageSize=1"),
        apiRequest<CollectionResponse>("/api/ai-router/tasks?page=1&pageSize=1&status=in_progress"),
        apiRequest<CollectionResponse>("/api/comfy/servers?page=1&pageSize=10"),
        apiRequest<CollectionResponse>("/api/comfy/servers?page=1&pageSize=10&status=ready"),
        apiRequest<CollectionResponse>("/api/comfy/operations?page=1&pageSize=1&status=failed"),
        apiRequest<CollectionResponse>("/api/comfy/operations?page=1&pageSize=5"),
      ]);
      setSnapshot({
        models: Array.isArray(models.data) ? models.data.length : 0,
        tasks: numberAt(tasks, "total"),
        activeTasks: numberAt(active, "total"),
        servers: numberAt(servers, "total"),
        readyServers: numberAt(ready, "total"),
        failedOperations: numberAt(failed, "total"),
        recentOperations: Array.isArray(operations.operations) ? operations.operations.filter(isRecord) : [],
      });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load operational summary");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return <>
    <PageHeader title="Overview" description="Current router and ComfyUI control-plane state." onRefresh={() => void load()} refreshing={loading} />
    {error ? <div className="error-banner">{error}</div> : null}
    <section className="metric-grid" aria-busy={loading}>
      <Metric icon={<Boxes />} label="Configured models" value={snapshot?.models} />
      <Metric icon={<Cpu />} label="Router tasks" value={snapshot?.tasks} detail={`${snapshot?.activeTasks ?? 0} in progress`} />
      <Metric icon={<Server />} label="Managed servers" value={snapshot?.servers} detail={`${snapshot?.readyServers ?? 0} ready`} />
      <Metric icon={snapshot?.failedOperations ? <TriangleAlert /> : <CheckCircle2 />} label="Failed operations" value={snapshot?.failedOperations} warning={Boolean(snapshot?.failedOperations)} />
    </section>
    <section className="overview-section">
      <header><h2>Recent operations</h2></header>
      {snapshot?.recentOperations.length ? <div className="operation-list">{snapshot.recentOperations.map((operation) => <div key={String(operation.operationId)}><div><strong>{String(operation.kind)}</strong><span>Server {String(operation.serverId)}</span></div><StatusBadge value={operation.status} /><time>{formatDate(operation.updatedAt)}</time></div>)}</div> : <div className="empty-state">No recent operations.</div>}
    </section>
  </>;
}

function Metric({ icon, label, value, detail, warning }: { icon: React.ReactNode; label: string; value?: number; detail?: string; warning?: boolean }) {
  return <article className={`metric ${warning ? "metric-warning" : ""}`}><div className="metric-icon">{icon}</div><div><span>{label}</span><strong>{value ?? "—"}</strong>{detail ? <small>{detail}</small> : null}</div></article>;
}
function numberAt(value: CollectionResponse, key: string): number { return typeof value[key] === "number" ? value[key] as number : 0; }
function isRecord(value: unknown): value is DataRecord { return Boolean(value) && typeof value === "object" && !Array.isArray(value); }
function formatDate(value: unknown): string { const date = new Date(String(value)); return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(); }
